import { Box, Circle, Divider, Flex, Image, Text } from '@chakra-ui/react';
import parseString from '../../../../../hooks/useLinkFormatter';
import { useMemo } from 'react';
import googleBackButtons from '../../../../../assets/vectors/googleBackButtons.png';
import { PreviewData } from '../../../../../store/types/template';
import { PassNotification } from '../shared/PassNotification';
import { getBarcodePreviewConfig } from '../../utils/googlePassPreview';
import { isDuplicateBackField } from '../../utils/googlePassPreview';
import BarcodePreview from '../BarcodePreview';

const GooglePassBack = ({
  previewData,
  createTemplate,
  notifOpen,
}: {
  previewData: PreviewData;
  createTemplate: boolean;
  notifOpen: boolean;
}) => {
  const {
    image: previewBarcodeImage,
    width: previewBarcodeImageWidth,
    height: previewBarcodeImageHeight,
  } = useMemo(
    () => getBarcodePreviewConfig(previewData),
    [previewData?.barcode?.type]
  );

  const isBarcodeFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('barcode.enabled') ||
      previewData.isFieldHighlighted?.('barcode.type') ||
      previewData.isFieldHighlighted?.('barcode.source') ||
      previewData.isFieldHighlighted?.('barcode.value') ||
      previewData.isFieldHighlighted?.('barcode.displayText')
    );
  }, [previewData.isFieldHighlighted]);

  const duplicateBackfield = (field: string) =>
    isDuplicateBackField(previewData, field);

  return (
    <>
      {/* NOTIFICATION */}
      <PassNotification
        open={notifOpen}
        autoHideMs={0}
        title={previewData?.notificationHeader || 'Notification header'}
        subtitle={previewData?.notificationMessage ?? 'Your message text here'}
        imageUrl={previewData.googleIconLogo || previewData.iconLogo}
        inset="12% 3.5% auto 3%"
        device="google"
      />

      {/* BACK CONTENT */}
      <Box position="absolute" inset="11% 5% 10% 5%">
        {/* {(createView || isEdit || clientView || createTemplate) && ( */}
        {createTemplate && (
          <>
            <Box
              borderTop="0px"
              maxW="full"
              m="0 auto"
              position="relative"
              p="15px"
              className="androidGPayDetailsBorderHelper"
              h="600px"
              overflowY="scroll"
              overflowX="hidden"
              sx={{
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {/* BARCODE */}
              {/* {(createView || clientView || isEdit || createTemplate) && */}
              {createTemplate &&
                previewBarcodeImage &&
                !!previewData.barcode.enabled &&
                !!previewData.barcode.type &&
                previewData.googleNfcEnabled && (
                  <>
                    <Flex mt={3} mb={2} w="full" justifyContent="center">
                      <Box
                        w="fit-content"
                        borderRadius="4px"
                        border={
                          isBarcodeFieldHighlighted
                            ? '2px solid #FF4935'
                            : 'none'
                        }
                        p={isBarcodeFieldHighlighted ? '4px' : '0'}
                      >
                        <BarcodePreview
                          type={previewData.barcode.type}
                          value={previewData.barcode.value}
                        />
                      </Box>
                    </Flex>

                    {/* BARCODE DISPLAY TEXT */}
                    {previewData.barcode.displayText &&
                      (previewData.barcode.source !== 'custom' ||
                        (previewData.barcode.source === 'custom' &&
                          previewData.barcode.value)) && (
                        <>
                          <Flex w="full" justifyContent="center" mb={3} mt={2}>
                            <Box
                              w="fit-content"
                              borderRadius="4px"
                              border="none"
                              p="0"
                            >
                              <Text
                                fontSize="13px"
                                textStyle="headingFamilyRegularApplePay"
                              >
                                {previewData.barcode.value !== ''
                                  ? previewData.barcode.value
                                  : previewData.barcode.source === 'custom'
                                    ? ''
                                    : previewData.barcode.source?.toUpperCase()}
                              </Text>
                            </Box>
                          </Flex>
                        </>
                      )}
                  </>
                )}

              {/* ICON */}
              <Box textAlign="left">
                <Circle size="30px" border="1px">
                  <Image
                    borderRadius="100%"
                    boxSize="full"
                    objectFit="cover"
                    src={previewData.googleIconLogo || previewData.iconLogo}
                    alt=""
                  />
                </Circle>
              </Box>

              {/* CENTRAL TITLE */}
              {previewData.centralTitle &&
                (previewData.passType === 'loyalty' ||
                  previewData.passType === 'stampCard' ||
                  previewData.passType === 'eventTicket') && (
                  <Text
                    fontSize="20px"
                    textStyle="headingFamilyRegularGooglePay"
                    textAlign="left"
                    color="androidGPayLabel"
                    wordBreak="break-word"
                    mt={3}
                  >
                    {previewData.centralTitle}
                  </Text>
                )}

              {/* HEADER ONE */}
              {previewData.passType !== 'generic' && (
                <>
                  {previewData.headerOneId ||
                  previewData.isFieldHighlighted?.('header.one.id') ||
                  previewData.isFieldHighlighted?.('header.one.label') ||
                  previewData.isFieldHighlighted?.('header.one.defaultValue') ? (
                    <Box pt="15px">
                      <Text
                        fontSize="12px"
                        textStyle="headingFamilySemiBoldGooglePay"
                        textAlign="left"
                        color="androidGPayLabel"
                        pb="5px"
                      >
                        {previewData.headerOneLabel
                          ? previewData.headerOneLabel
                          : ''}
                      </Text>
                      <Text
                        fontSize="12px"
                        textStyle="headingFamilyRegularGooglePay"
                        textAlign="left"
                        color="androidGPayContent"
                        wordBreak="break-word"
                      >
                        {previewData.headerOneValue
                          ? previewData.headerOneValue.toString()
                          : previewData.hideIdPlaceholder ? '' : `<${previewData.headerOneId}>`}
                      </Text>
                    </Box>
                  ) : (
                    ''
                  )}
                </>
              )}

              {/* DETAILS ONE */}
              {!duplicateBackfield('detailsOne') &&
              (previewData.detailsOneId ||
                previewData.detailsOneLabel ||
                previewData.detailsOneValue ||
                previewData.isFieldHighlighted?.('details.one.id') ||
                previewData.isFieldHighlighted?.('details.one.label') ||
                previewData.isFieldHighlighted?.('details.one.defaultValue')) ? (
                <Box pt="15px">
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilySemiBoldGooglePay"
                    textAlign="left"
                    color="androidGPayLabel"
                    pb="5px"
                  >
                    {previewData.detailsOneLabel}
                  </Text>
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilyRegularGooglePay"
                    textAlign="left"
                    color="androidGPayContent"
                    wordBreak="break-word"
                  >
                    {!!previewData.detailsOneId && !previewData.detailsOneValue && !previewData.hideIdPlaceholder
                      ? '<' + previewData.detailsOneId + '>'
                      : previewData.detailsOneValue
                        ? previewData.detailsOneValue
                        : ''}
                  </Text>
                </Box>
              ) : (
                ''
              )}

              {/* DETAILS TWO */}
              {!duplicateBackfield('detailsTwo') &&
              (previewData.detailsTwoId ||
                previewData.detailsTwoLabel ||
                previewData.detailsTwoValue ||
                previewData.isFieldHighlighted?.('details.two.id') ||
                previewData.isFieldHighlighted?.('details.two.label') ||
                previewData.isFieldHighlighted?.('details.two.defaultValue')) ? (
                <Box pt="15px">
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilySemiBoldGooglePay"
                    textAlign="left"
                    color="androidGPayLabel"
                    pb="5px"
                  >
                    {previewData.detailsTwoLabel}
                  </Text>
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilyRegularGooglePay"
                    textAlign="left"
                    color="androidGPayContent"
                    wordBreak="break-word"
                  >
                    {!!previewData.detailsTwoId && !previewData.detailsTwoValue && !previewData.hideIdPlaceholder
                      ? '<' + previewData.detailsTwoId + '>'
                      : previewData.detailsTwoValue
                        ? previewData.detailsTwoValue
                        : ''}
                  </Text>
                </Box>
              ) : (
                ''
              )}

              {/* DETAILS THREE */}
              {!duplicateBackfield('detailsThree') &&
              (previewData.detailsThreeId ||
                previewData.detailsThreeLabel ||
                previewData.detailsThreeValue ||
                previewData.isFieldHighlighted?.('details.three.id') ||
                previewData.isFieldHighlighted?.('details.three.label') ||
                previewData.isFieldHighlighted?.('details.three.defaultValue')) ? (
                <Box pt="15px">
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilySemiBoldGooglePay"
                    textAlign="left"
                    color="androidGPayLabel"
                    pb="5px"
                  >
                    {previewData.detailsThreeLabel}
                  </Text>
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilyRegularGooglePay"
                    textAlign="left"
                    color="androidGPayContent"
                    wordBreak="break-word"
                  >
                    {!!previewData.detailsThreeId &&
                    !previewData.detailsThreeValue && !previewData.hideIdPlaceholder
                      ? '<' + previewData.detailsThreeId + '>'
                      : previewData.detailsThreeValue
                        ? previewData.detailsThreeValue
                        : ''}
                  </Text>
                </Box>
              ) : (
                ''
              )}

              {/* DETAILS FOUR */}
              {!duplicateBackfield('detailsFour') &&
              (previewData.detailsFourId ||
                previewData.detailsFourLabel ||
                previewData.detailsFourValue ||
                previewData.isFieldHighlighted?.('details.four.id') ||
                previewData.isFieldHighlighted?.('details.four.label') ||
                previewData.isFieldHighlighted?.('details.four.defaultValue')) ? (
                <Box pt="15px">
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilySemiBoldGooglePay"
                    textAlign="left"
                    color="androidGPayLabel"
                    pb="5px"
                  >
                    {previewData.detailsFourLabel}
                  </Text>
                  <Text
                    fontSize="12px"
                    textStyle="headingFamilyRegularGooglePay"
                    textAlign="left"
                    color="androidGPayContent"
                    wordBreak="break-word"
                  >
                    {!!previewData.detailsFourId &&
                    !previewData.detailsFourValue && !previewData.hideIdPlaceholder
                      ? '<' + previewData.detailsFourId + '>'
                      : previewData.detailsFourValue
                        ? previewData.detailsFourValue
                        : ''}
                  </Text>
                </Box>
              ) : (
                ''
              )}
              {previewData.passType !== 'loyalty' &&
                previewData.passType !== 'stampCard' && (
                  <>
                    {/* DETAILS FIVE */}
                    {!duplicateBackfield('detailsFive') &&
                    (previewData.detailsFiveId ||
                      previewData.detailsFiveLabel ||
                      previewData.detailsFiveValue ||
                      previewData.isFieldHighlighted?.('details.five.id') ||
                      previewData.isFieldHighlighted?.('details.five.label') ||
                      previewData.isFieldHighlighted?.('details.five.defaultValue')) ? (
                      <Box pt="15px">
                        <Text
                          fontSize="12px"
                          textStyle="headingFamilySemiBoldGooglePay"
                          textAlign="left"
                          color="androidGPayLabel"
                          pb="5px"
                        >
                          {previewData.detailsFiveLabel}
                        </Text>
                        <Text
                          fontSize="12px"
                          textStyle="headingFamilyRegularGooglePay"
                          textAlign="left"
                          color="androidGPayContent"
                          wordBreak="break-word"
                        >
                          {!!previewData.detailsFiveId &&
                          !previewData.detailsFiveValue && !previewData.hideIdPlaceholder
                            ? '<' + previewData.detailsFiveId + '>'
                            : previewData.detailsFiveValue
                              ? previewData.detailsFiveValue
                              : ''}
                        </Text>
                      </Box>
                    ) : (
                      ''
                    )}

                    {/* DETAILS SIX */}
                    {!duplicateBackfield('detailsSix') &&
                    (previewData.detailsSixId ||
                      previewData.detailsSixLabel ||
                      previewData.detailsSixValue ||
                      previewData.isFieldHighlighted?.('details.six.id') ||
                      previewData.isFieldHighlighted?.('details.six.label') ||
                      previewData.isFieldHighlighted?.('details.six.defaultValue')) ? (
                      <Box pt="15px">
                        <Text
                          fontSize="12px"
                          textStyle="headingFamilySemiBoldGooglePay"
                          textAlign="left"
                          color="androidGPayLabel"
                          pb="5px"
                        >
                          {previewData.detailsSixLabel}
                        </Text>
                        <Text
                          fontSize="12px"
                          textStyle="headingFamilyRegularGooglePay"
                          textAlign="left"
                          color="androidGPayContent"
                          wordBreak="break-word"
                        >
                          {!!previewData.detailsSixId &&
                          !previewData.detailsSixValue && !previewData.hideIdPlaceholder
                            ? '<' + previewData.detailsSixId + '>'
                            : previewData.detailsSixValue
                              ? previewData.detailsSixValue
                              : ''}
                        </Text>
                      </Box>
                    ) : (
                      ''
                    )}
                  </>
                )}

              {/* BACK ONE */}
              {previewData.backOneLabel ||
              previewData.backOneValue ||
              previewData.isFieldHighlighted?.('back.one.id') ||
              previewData.isFieldHighlighted?.('back.one.label') ||
              previewData.isFieldHighlighted?.('back.one.defaultValue') ? (
                <Box pt="15px">
                  <Box
                    width={
                      (previewData.isFieldHighlighted?.('back.one.id') ||
                        previewData.isFieldHighlighted?.('back.one.label') ||
                        previewData.isFieldHighlighted?.('back.one.defaultValue')) &&
                      !previewData.backOneLabel && !previewData.backOneValue &&
                      !(!!previewData.backOneId && !previewData.hideIdPlaceholder)
                        ? '100%'
                        : 'fit-content'
                    }
                    minH={
                      (previewData.isFieldHighlighted?.('back.one.id') ||
                        previewData.isFieldHighlighted?.('back.one.label') ||
                        previewData.isFieldHighlighted?.('back.one.defaultValue')) &&
                      !previewData.backOneLabel && !previewData.backOneValue &&
                      !(!!previewData.backOneId && !previewData.hideIdPlaceholder)
                        ? '28px'
                        : undefined
                    }
                    border={
                      previewData.isFieldHighlighted?.('back.one.id') ||
                      previewData.isFieldHighlighted?.('back.one.label') ||
                      previewData.isFieldHighlighted?.('back.one.defaultValue')
                        ? '2px solid #FF4935'
                        : 'none'
                    }
                    borderRadius="4px"
                    p={
                      (previewData.isFieldHighlighted?.('back.one.id') ||
                        previewData.isFieldHighlighted?.('back.one.label') ||
                        previewData.isFieldHighlighted?.(
                          'back.one.defaultValue'
                        )) &&
                      '4px'
                    }
                  >
                    <Text
                      fontSize="12px"
                      textStyle="headingFamilySemiBoldGooglePay"
                      textAlign="left"
                      color="androidGPayLabel"
                      pb="5px"
                    >
                      {previewData.backOneLabel ? previewData.backOneLabel : ''}
                    </Text>
                    {!!previewData.backOneId && !previewData.backOneValue && !previewData.hideIdPlaceholder ? (
                      <Text
                        fontSize="13px"
                        textStyle="headingFamilyRegularGooglePay"
                        color="androidGPayContent"
                        textAlign="left"
                        wordBreak="break-word"
                      >
                        {'<' + previewData.backOneId + '>'}
                      </Text>
                    ) : (
                      <>
                        {previewData.backOneValue.split('\\n').map((i, key) => {
                          return (
                            <Text
                              fontSize="13px"
                              textStyle="headingFamilyRegularGooglePay"
                              color="androidGPayContent"
                              textAlign="left"
                              wordBreak="break-word"
                              key={key}
                            >
                              {parseString(i, { linkRawUrls: false })}
                            </Text>
                          );
                        })}
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                ''
              )}

              {/* BACK TWO */}
              {previewData.backTwoLabel ||
              previewData.backTwoValue ||
              previewData.isFieldHighlighted?.('back.two.id') ||
              previewData.isFieldHighlighted?.('back.two.label') ||
              previewData.isFieldHighlighted?.('back.two.defaultValue') ? (
                <Box pt="15px">
                  <Box
                    width={
                      (previewData.isFieldHighlighted?.('back.two.id') ||
                        previewData.isFieldHighlighted?.('back.two.label') ||
                        previewData.isFieldHighlighted?.('back.two.defaultValue')) &&
                      !previewData.backTwoLabel && !previewData.backTwoValue &&
                      !(!!previewData.backTwoId && !previewData.hideIdPlaceholder)
                        ? '100%'
                        : 'fit-content'
                    }
                    minH={
                      (previewData.isFieldHighlighted?.('back.two.id') ||
                        previewData.isFieldHighlighted?.('back.two.label') ||
                        previewData.isFieldHighlighted?.('back.two.defaultValue')) &&
                      !previewData.backTwoLabel && !previewData.backTwoValue &&
                      !(!!previewData.backTwoId && !previewData.hideIdPlaceholder)
                        ? '28px'
                        : undefined
                    }
                    border={
                      previewData.isFieldHighlighted?.('back.two.id') ||
                      previewData.isFieldHighlighted?.('back.two.label') ||
                      previewData.isFieldHighlighted?.('back.two.defaultValue')
                        ? '2px solid #FF4935'
                        : 'none'
                    }
                    borderRadius="4px"
                    p={
                      (previewData.isFieldHighlighted?.('back.two.id') ||
                        previewData.isFieldHighlighted?.('back.two.label') ||
                        previewData.isFieldHighlighted?.(
                          'back.two.defaultValue'
                        )) &&
                      '4px'
                    }
                  >
                    <Text
                      fontSize="12px"
                      textStyle="headingFamilySemiBoldGooglePay"
                      textAlign="left"
                      color="androidGPayLabel"
                      pb="5px"
                    >
                      {previewData.backTwoLabel ? previewData.backTwoLabel : ''}
                    </Text>
                    {!!previewData.backTwoId && !previewData.backTwoValue && !previewData.hideIdPlaceholder ? (
                      <Text
                        fontSize="13px"
                        textStyle="headingFamilyRegularGooglePay"
                        color="androidGPayContent"
                        textAlign="left"
                        wordBreak="break-word"
                      >
                        {'<' + previewData.backTwoId + '>'}
                      </Text>
                    ) : (
                      <>
                        {previewData.backTwoValue.split('\\n').map((i, key) => {
                          return (
                            <Text
                              fontSize="13px"
                              textStyle="headingFamilyRegularGooglePay"
                              color="androidGPayContent"
                              textAlign="left"
                              wordBreak="break-word"
                              key={key}
                            >
                              {parseString(i, { linkRawUrls: false })}
                            </Text>
                          );
                        })}
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                ''
              )}

              {/* BACK THREE */}
              {previewData.backThreeLabel ||
              previewData.backThreeValue ||
              previewData.isFieldHighlighted?.('back.three.id') ||
              previewData.isFieldHighlighted?.('back.three.label') ||
              previewData.isFieldHighlighted?.('back.three.defaultValue') ? (
                <Box pt="15px">
                  <Box
                    width={
                      (previewData.isFieldHighlighted?.('back.three.id') ||
                        previewData.isFieldHighlighted?.('back.three.label') ||
                        previewData.isFieldHighlighted?.('back.three.defaultValue')) &&
                      !previewData.backThreeLabel && !previewData.backThreeValue &&
                      !(!!previewData.backThreeId && !previewData.hideIdPlaceholder)
                        ? '100%'
                        : 'fit-content'
                    }
                    minH={
                      (previewData.isFieldHighlighted?.('back.three.id') ||
                        previewData.isFieldHighlighted?.('back.three.label') ||
                        previewData.isFieldHighlighted?.('back.three.defaultValue')) &&
                      !previewData.backThreeLabel && !previewData.backThreeValue &&
                      !(!!previewData.backThreeId && !previewData.hideIdPlaceholder)
                        ? '28px'
                        : undefined
                    }
                    border={
                      previewData.isFieldHighlighted?.('back.three.id') ||
                      previewData.isFieldHighlighted?.('back.three.label') ||
                      previewData.isFieldHighlighted?.(
                        'back.three.defaultValue'
                      )
                        ? '2px solid #FF4935'
                        : 'none'
                    }
                    borderRadius="4px"
                    p={
                      (previewData.isFieldHighlighted?.('back.three.id') ||
                        previewData.isFieldHighlighted?.('back.three.label') ||
                        previewData.isFieldHighlighted?.(
                          'back.three.defaultValue'
                        )) &&
                      '4px'
                    }
                  >
                    <Text
                      fontSize="12px"
                      textStyle="headingFamilySemiBoldGooglePay"
                      textAlign="left"
                      color="androidGPayLabel"
                      pb="5px"
                    >
                      {previewData.backThreeLabel
                        ? previewData.backThreeLabel
                        : ''}
                    </Text>
                    {!!previewData.backThreeId &&
                    !previewData.backThreeValue && !previewData.hideIdPlaceholder ? (
                      <Text
                        fontSize="13px"
                        textStyle="headingFamilyRegularGooglePay"
                        color="androidGPayContent"
                        textAlign="left"
                        wordBreak="break-word"
                      >
                        {'<' + previewData.backThreeId + '>'}
                      </Text>
                    ) : (
                      <>
                        {previewData.backThreeValue
                          .split('\\n')
                          .map((i, key) => {
                            return (
                              <Text
                                fontSize="13px"
                                textStyle="headingFamilyRegularGooglePay"
                                color="androidGPayContent"
                                textAlign="left"
                                wordBreak="break-word"
                                key={key}
                              >
                                {parseString(i, { linkRawUrls: false })}
                              </Text>
                            );
                          })}
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                ''
              )}

              {/* BACK FOUR */}
              {previewData.backFourLabel ||
              previewData.backFourValue ||
              previewData.isFieldHighlighted?.('back.four.id') ||
              previewData.isFieldHighlighted?.('back.four.label') ||
              previewData.isFieldHighlighted?.('back.four.defaultValue') ? (
                <Box pt="15px">
                  <Box
                    width={
                      (previewData.isFieldHighlighted?.('back.four.id') ||
                        previewData.isFieldHighlighted?.('back.four.label') ||
                        previewData.isFieldHighlighted?.('back.four.defaultValue')) &&
                      !previewData.backFourLabel && !previewData.backFourValue &&
                      !(!!previewData.backFourId && !previewData.hideIdPlaceholder)
                        ? '100%'
                        : 'fit-content'
                    }
                    minH={
                      (previewData.isFieldHighlighted?.('back.four.id') ||
                        previewData.isFieldHighlighted?.('back.four.label') ||
                        previewData.isFieldHighlighted?.('back.four.defaultValue')) &&
                      !previewData.backFourLabel && !previewData.backFourValue &&
                      !(!!previewData.backFourId && !previewData.hideIdPlaceholder)
                        ? '28px'
                        : undefined
                    }
                    border={
                      previewData.isFieldHighlighted?.('back.four.id') ||
                      previewData.isFieldHighlighted?.('back.four.label') ||
                      previewData.isFieldHighlighted?.('back.four.defaultValue')
                        ? '2px solid #FF4935'
                        : 'none'
                    }
                    borderRadius="4px"
                    p={
                      (previewData.isFieldHighlighted?.('back.four.id') ||
                        previewData.isFieldHighlighted?.('back.four.label') ||
                        previewData.isFieldHighlighted?.(
                          'back.four.defaultValue'
                        )) &&
                      '4px'
                    }
                  >
                    <Text
                      fontSize="12px"
                      textStyle="headingFamilySemiBoldGooglePay"
                      textAlign="left"
                      color="androidGPayLabel"
                      pb="5px"
                    >
                      {previewData.backFourLabel
                        ? previewData.backFourLabel
                        : ''}
                    </Text>
                    {!!previewData.backFourId && !previewData.backFourValue && !previewData.hideIdPlaceholder ? (
                      <Text
                        fontSize="13px"
                        textStyle="headingFamilyRegularGooglePay"
                        color="androidGPayContent"
                        textAlign="left"
                        wordBreak="break-word"
                      >
                        {'<' + previewData.backFourId + '>'}
                      </Text>
                    ) : (
                      <>
                        {previewData.backFourValue
                          .split('\\n')
                          .map((i, key) => {
                            return (
                              <Text
                                fontSize="13px"
                                textStyle="headingFamilyRegularGooglePay"
                                color="androidGPayContent"
                                textAlign="left"
                                wordBreak="break-word"
                                key={key}
                              >
                                {parseString(i, { linkRawUrls: false })}
                              </Text>
                            );
                          })}
                      </>
                    )}
                  </Box>
                </Box>
              ) : (
                ''
              )}

              {/* BACK FIVE */}
              {previewData.backFiveLabel ||
              previewData.backFiveValue ||
              previewData.isFieldHighlighted?.('back.five.id') ||
              previewData.isFieldHighlighted?.('back.five.label') ||
              previewData.isFieldHighlighted?.('back.five.defaultValue') ? (
                <>
                  <Box pt="15px">
                    <Box
                      width={
                        (previewData.isFieldHighlighted?.('back.five.id') ||
                          previewData.isFieldHighlighted?.('back.five.label') ||
                          previewData.isFieldHighlighted?.('back.five.defaultValue')) &&
                        !previewData.backFiveLabel && !previewData.backFiveValue &&
                        !(!!previewData.backFiveId && !previewData.hideIdPlaceholder)
                          ? '100%'
                          : 'fit-content'
                      }
                      minH={
                        (previewData.isFieldHighlighted?.('back.five.id') ||
                          previewData.isFieldHighlighted?.('back.five.label') ||
                          previewData.isFieldHighlighted?.('back.five.defaultValue')) &&
                        !previewData.backFiveLabel && !previewData.backFiveValue &&
                        !(!!previewData.backFiveId && !previewData.hideIdPlaceholder)
                          ? '28px'
                          : undefined
                      }
                      border={
                        previewData.isFieldHighlighted?.('back.five.id') ||
                        previewData.isFieldHighlighted?.('back.five.label') ||
                        previewData.isFieldHighlighted?.(
                          'back.five.defaultValue'
                        )
                          ? '2px solid #FF4935'
                          : 'none'
                      }
                      borderRadius="4px"
                      p={
                        (previewData.isFieldHighlighted?.('back.five.id') ||
                          previewData.isFieldHighlighted?.('back.five.label') ||
                          previewData.isFieldHighlighted?.(
                            'back.five.defaultValue'
                          )) &&
                        '4px'
                      }
                    >
                      <Text
                        fontSize="12px"
                        textStyle="headingFamilySemiBoldGooglePay"
                        textAlign="left"
                        color="androidGPayLabel"
                        pb="5px"
                      >
                        {previewData.backFiveLabel
                          ? previewData.backFiveLabel
                          : ''}
                      </Text>
                      {!!previewData.backFiveId &&
                      !previewData.backFiveValue && !previewData.hideIdPlaceholder ? (
                        <Text
                          fontSize="13px"
                          textStyle="headingFamilyRegularGooglePay"
                          color="androidGPayContent"
                          textAlign="left"
                          wordBreak="break-word"
                        >
                          {'<' + previewData.backFiveId + '>'}
                        </Text>
                      ) : (
                        <>
                          {previewData.backFiveValue
                            .split('\\n')
                            .map((i, key) => {
                              return (
                                <Text
                                  fontSize="13px"
                                  textStyle="headingFamilyRegularGooglePay"
                                  color="androidGPayContent"
                                  textAlign="left"
                                  wordBreak="break-word"
                                  key={key}
                                >
                                  {parseString(i, { linkRawUrls: false })}
                                </Text>
                              );
                            })}
                        </>
                      )}
                    </Box>
                  </Box>
                </>
              ) : (
                ''
              )}

              {/* CONTROL BUTTONS */}
              <Image
                src={googleBackButtons}
                alt="Google Back Buttons"
                w="35%"
                mt={3}
              />

              {/* PASSENTRY TERMS */}
              <Box pt="15px">
                <Text
                  fontSize="12px"
                  textStyle="headingFamilySemiBoldGooglePay"
                  textAlign="left"
                  color="androidGPayLabel"
                  pb="5px"
                >
                  PassEntry Terms
                </Text>
                <Text
                  fontSize="12px"
                  textStyle="headingFamilyRegularGooglePay"
                  textAlign="left"
                  color="androidGPayLabel"
                  wordBreak="break-word"
                >
                  {/* {previewData.googleTermsText
                    ? `${previewData.googleTermsText}`
                    : 'Your use of the Google Wallet is subject to Google’s terms and conditions of use, which PassEntry are not responsible for.'} */}
                  Your use of the Google Wallet is subject to Google’s terms and
                  conditions of use, which PassEntry are not responsible for.
                </Text>
              </Box>

              {/* GOOGLE TERMS */}
              <Box pt="15px">
                <Text
                  fontSize="12px"
                  textStyle="headingFamilyRegularGooglePay"
                  textAlign="left"
                  color="androidGPayContent"
                  pb="5px"
                >
                  The Pass provider or merchant is responsible for the info on
                  this pass and may send you notifications. Contact them with
                  any questions.
                </Text>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* BAR ICON */}
      <Flex
        position="absolute"
        inset="91% 10% auto 0"
        my={3}
        width="full"
        justifyContent="center"
        direction="row"
        align="center"
      >
        <Divider
          orientation="vertical"
          borderColor="#656565"
          w="25%"
          borderWidth="2px"
          borderRadius="25px"
        />
      </Flex>
    </>
  );
};

export default GooglePassBack;
